import { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogAction,
  DialogCancel,
  DialogContent,
  DialogFooter,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Banner, BannerDescription } from '@wartek-id/banner-v1'

import { TableContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/TableContext'
import Dot from 'components/Dot/Dot'
import { LabelPredikatKinerja } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/LabelPredikatKinerja'
import {
  PERIODIK_PERFORMANCE_PRACTICE_RESULTS,
  PERIODIK_PERFORMANCE_PREDICATE_RATING_MAPPING,
  PERIODIK_WORK_BEHAVIOR_RESULTS,
} from 'app/KinerjaShared/constants/periodik'
import { OnboardingContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding/OnboardingContext'
import {
  ONBOARDING_STEP_INDEXES,
  STEP_IDS,
} from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding/steps'

import type {
  PenetapanPredikatKinerjaComponent,
  RequestSavePenetapanPegawai,
} from 'types/kinerja-tim-penilai-ps'

type FormFields = Pick<
  PenetapanPredikatKinerjaComponent,
  'praktikKinerjaRating' | 'perilakuKerjaRating'
>

type FormProps = {
  isDisabled?: boolean
  isSubmitting?: boolean
  isSubmitSuccess?: boolean
  onSubmit?: (payload: RequestSavePenetapanPegawai) => void
}

export function ActionBulkPenetapan({
  isDisabled: disabled = false,
  isSubmitting,
  isSubmitSuccess,
  onSubmit,
}: FormProps) {
  const context = useContext(TableContext)
  const { stepIndex, onboardingMode } = useContext(OnboardingContext)

  // Ensure that the component is used within the correct context provider
  if (!context)
    throw new Error(
      '<TablePegawaiPenetapan.ActionBulkPenetapan> must be used within a <TablePegawaiPenetapan.Provider>'
    )

  // ------------------------------------
  // Get context data and its derivatives
  // ------------------------------------
  const { selectedIds, pengawasSekolahList, onToggleAll } = context

  const numOfSelected = selectedIds.size
  const isDisabled = useMemo(() => {
    if (onboardingMode && stepIndex !== ONBOARDING_STEP_INDEXES.SET_PENILAIAN) {
      return true
    }

    return !onboardingMode && (numOfSelected <= 0 || disabled)
  }, [disabled, numOfSelected, onboardingMode, stepIndex])
  const selectedPengawasSekolah = pengawasSekolahList.filter((item) =>
    selectedIds.has(item.pengawasSekolahId)
  )
  // Slice data into 2 column
  const middleIndex = Math.ceil(selectedPengawasSekolah.length / 2) // round up for odd-length arrays
  const firstHalfData = selectedPengawasSekolah.slice(0, middleIndex)
  const secondHalfData = selectedPengawasSekolah.slice(middleIndex)

  // -----------------
  // Handle form sheet
  // -----------------
  const [isFormOpen, setFormOpen] = useState(false)
  const handleCloseForm = () => {
    setFormOpen(false)
  }

  const handleOpenForm = () => {
    setFormOpen(true)
  }

  // ------------------
  // Handle form fields
  // ------------------
  const formMethods = useForm<FormFields>({ mode: 'onChange' })
  const [praktikKinerjaRating, perilakuKerjaRating] = formMethods.watch([
    'praktikKinerjaRating',
    'perilakuKerjaRating',
  ])
  const predikatKinerja =
    PERIODIK_PERFORMANCE_PREDICATE_RATING_MAPPING[
      `${praktikKinerjaRating}-${perilakuKerjaRating}`
    ]
  const handleSubmit = (values: FormFields) => {
    onSubmit?.({
      ...values,
      pengawasSekolahIds: Array.from(selectedIds),
      predikatKinerja,
    })
  }
  useEffect(() => {
    if (!isFormOpen) {
      formMethods.reset()
    }
  }, [formMethods, isFormOpen])

  // Upon successful submit: close the form and reset selected items
  useEffect(() => {
    if (isSubmitSuccess) {
      handleCloseForm()
      onToggleAll(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccess])

  return (
    <>
      <Button
        id={STEP_IDS.OPEN_FORM_BTN_ID}
        color={isDisabled ? 'blue' : 'white'}
        disabled={isDisabled}
        onClick={handleOpenForm}
      >
        Lakukan Sekaligus
      </Button>

      <Dialog isOpen={isFormOpen} onClose={handleCloseForm} size="md">
        <DialogOverlay />
        <DialogContent className="!rounded-xl">
          <div className="rounded-t-lg bg-blue-80">
            <Text variant="heading-lg" color="inverse" className="p-6">
              Lakukan Sekaligus
            </Text>
          </div>
          <div className="p-5 space-y-6">
            <Banner
              animationDuration={300}
              onOpenStateChange={function gs() {}}
              title="Rating akan disimpan untuk semua Pengawas Sekolah yang dipilih"
              type="warning"
              closable={false}
            >
              <BannerDescription>
                Pastikan Anda sudah cek rekomendasi dan mempertimbangkan kinerja
                tiap Pengawas Sekolah.
              </BannerDescription>
            </Banner>

            <div className="rounded-md bg-surface-subdued">
              <div className="p-6">
                <Text variant="heading-sm">Nama yang Dipilih:</Text>
              </div>

              <div className="grid grid-cols-2 gap-4 p-6 pt-0 pr-0 overflow-auto max-h-40">
                {[firstHalfData, secondHalfData].map((data, key) => (
                  <ul key={key}>
                    {data.map((item, index) => {
                      return (
                        <li
                          key={`selected-ps-${index}`}
                          className="flex items-center"
                        >
                          <Dot />
                          {item.namaPengawas}
                        </li>
                      )
                    })}
                  </ul>
                ))}
              </div>
            </div>

            <form className="space-y-5">
              <fieldset>
                <Text
                  as="label"
                  htmlFor="rating-praktik-kinerja"
                  variant="heading-sm"
                  className="block mb-2"
                >
                  Rating Praktik Kinerja
                </Text>
                <Controller
                  control={formMethods.control}
                  rules={{ required: true }}
                  name="praktikKinerjaRating"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        id="rating-praktik-kinerja"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={isDisabled}
                      >
                        <option
                          disabled
                          className="text-disabled"
                          selected
                          value=""
                        >
                          Pilih di sini
                        </option>
                        {Object.entries(
                          PERIODIK_PERFORMANCE_PRACTICE_RESULTS
                        ).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    )
                  }}
                />
              </fieldset>
              <fieldset>
                <Text
                  as="label"
                  htmlFor="rating-perilaku-kerja"
                  variant="heading-sm"
                  className="block mb-2"
                >
                  Rating Perilaku Kerja
                </Text>
                <Controller
                  control={formMethods.control}
                  rules={{ required: true }}
                  name="perilakuKerjaRating"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        id="rating-perilaku-kerja"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={isDisabled}
                      >
                        <option
                          disabled
                          className="text-disabled"
                          selected
                          value=""
                        >
                          Pilih di sini
                        </option>
                        {Object.entries(PERIODIK_WORK_BEHAVIOR_RESULTS).map(
                          ([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          )
                        )}
                      </Select>
                    )
                  }}
                />
              </fieldset>
              <fieldset>
                <Text
                  as="label"
                  htmlFor="rating-perilaku-kerja"
                  variant="heading-sm"
                  className="block mb-2"
                >
                  Predikat Kinerja
                </Text>

                <LabelPredikatKinerja value={predikatKinerja} />
              </fieldset>
            </form>
          </div>
          <DialogFooter className="!justify-between !items-center">
            <Text variant="heading-md">{selectedIds.size} Nama Dipilih</Text>
            <div className="flex space-x-3">
              <DialogCancel as={Button} color="white" onClick={handleCloseForm}>
                Kembali
              </DialogCancel>
              <DialogAction
                as={Button}
                onClick={formMethods.handleSubmit(handleSubmit)}
                color="blue"
                isLoading={isSubmitting}
                disabled={!formMethods.formState.isValid || isSubmitting}
                type="submit"
              >
                Simpan
              </DialogAction>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
